import { useState } from "react"

import type { UseCaseData } from "../../types/use-case.types"
import type { DirectorySearchResult } from "@/services/directory-search-service"

import { DirectorySearch } from "@/components/directory-search"
import { Globe, Share2, Trash2, UserPlus } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"

import { useSetUnrestricted } from "../../hooks/use-publish-use-case"
import {
  useAddUseCaseParticipant,
  useRemoveUseCaseParticipant,
  useUpdateUseCaseParticipantRole,
  useUseCaseParticipants,
} from "../../hooks/use-use-case-participants"

type UseCaseShareDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  useCaseId: string
  useCase: UseCaseData | undefined
}

type AddableRole = "reader" | "editor"

export function UseCaseShareDialog({
  open,
  onOpenChange,
  useCaseId,
  useCase,
}: UseCaseShareDialogProps) {
  const [newParticipantRole, setNewParticipantRole] = useState<AddableRole>("reader")

  const { data: participantsData, isLoading } = useUseCaseParticipants(useCaseId)
  const addParticipant = useAddUseCaseParticipant()
  const updateRole = useUpdateUseCaseParticipantRole()
  const removeParticipant = useRemoveUseCaseParticipant()

  const isOwner = useCase?.currentUserRole === "owner"
  const isUnrestricted = useCase?.isUnrestricted ?? false
  const setUnrestrictedMutation = useSetUnrestricted()

  const participants =
    (
      participantsData as
        | {
            participants?: Array<{
              oid: string
              displayName: string
              type: string
              role: string
              isOriginalOwner?: boolean
            }>
          }
        | undefined
    )?.participants ?? []
  const ownerUserId = (participantsData as { ownerUserId?: string } | undefined)?.ownerUserId

  const existingOids = participants.map((p) => p.oid).filter(Boolean) as string[]

  const handleAddParticipant = async (result: DirectorySearchResult) => {
    if (existingOids.includes(result.oid)) return

    await addParticipant.mutateAsync({
      useCaseId,
      participant: {
        oid: result.oid,
        displayName: result.displayName,
        type: result.type,
        role: newParticipantRole,
      },
    })
  }

  const handleRoleChange = async (participantOid: string, role: string) => {
    await updateRole.mutateAsync({ useCaseId, participantOid, role })
  }

  const handleRemove = async (participantOid: string) => {
    await removeParticipant.mutateAsync({ useCaseId, participantOid })
  }

  // Sort: owner first, then alphabetically
  const sortedParticipants = [...participants].sort((a, b) => {
    if (a.oid === ownerUserId) return -1
    if (b.oid === ownerUserId) return 1
    return (a.displayName || "").localeCompare(b.displayName || "")
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="size-5" />
            Share Use Case
          </DialogTitle>
          <DialogDescription>
            Add users or groups to collaborate on this use case.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {isOwner && (
            <div className="flex items-center justify-between rounded-md border px-3 py-2">
              <div className="flex items-center gap-2">
                <Globe className="text-muted-foreground size-4" />
                <div>
                  <p className="text-sm font-medium">Visible to everyone</p>
                  <p className="text-muted-foreground text-xs">
                    Anyone with module access can view this use case
                  </p>
                </div>
              </div>
              <button
                aria-checked={isUnrestricted}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 transition-colors after:absolute after:inset-x-0 after:-inset-y-0.5 after:content-[''] ${isUnrestricted ? "border-foreground bg-primary" : "bg-input border-transparent"}`}
                disabled={setUnrestrictedMutation.isPending}
                role="switch"
                onClick={() =>
                  setUnrestrictedMutation.mutate({
                    id: useCaseId,
                    isUnrestricted: !isUnrestricted,
                  })
                }
              >
                <span
                  className={`bg-background pointer-events-none block size-4 rounded-full shadow-lg ring-0 transition-transform ${isUnrestricted ? "translate-x-4" : "translate-x-0"}`}
                />
              </button>
            </div>
          )}

          {isOwner && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <UserPlus className="text-muted-foreground size-4" />
                <Label className="text-sm font-medium">Add People</Label>
              </div>

              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <DirectorySearch
                    disabled={addParticipant.isPending}
                    selectedOids={existingOids}
                    onSelect={handleAddParticipant}
                  />
                </div>
                <Select
                  value={newParticipantRole}
                  onValueChange={(v) => setNewParticipantRole(v as AddableRole)}
                >
                  <SelectTrigger className="w-[110px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reader">Reader</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />
            </div>
          )}

          <div className="space-y-3">
            <Label className="text-sm font-medium">People with Access</Label>
            {isLoading ? (
              <p className="text-muted-foreground text-sm">Loading...</p>
            ) : (
              <ScrollArea className="h-[min(300px,_50dvh)]">
                <div className="space-y-2">
                  {sortedParticipants.map((p) => {
                    const isOriginalOwner = p.oid === ownerUserId
                    return (
                      <div
                        key={p.oid}
                        className="flex items-center justify-between rounded-md border px-3 py-2"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{p.displayName}</span>
                          {p.type === "group" && (
                            <Badge className="text-[10px]" variant="secondary">
                              Group
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {isOriginalOwner && <Badge variant="outline">Owner</Badge>}
                          {!isOriginalOwner && isOwner && (
                            <>
                              <Select
                                value={p.role}
                                onValueChange={(v) => handleRoleChange(p.oid, v)}
                              >
                                <SelectTrigger className="h-7 w-[100px] text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="reader">Reader</SelectItem>
                                  <SelectItem value="editor">Editor</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button
                                className="size-7"
                                size="icon"
                                variant="ghost"
                                onClick={() => handleRemove(p.oid)}
                              >
                                <Trash2 className="text-destructive size-3.5" />
                              </Button>
                            </>
                          )}
                          {!isOriginalOwner && !isOwner && (
                            <Badge variant="secondary">{p.role}</Badge>
                          )}
                        </div>
                      </div>
                    )
                  })}
                  {sortedParticipants.length === 0 && (
                    <p className="text-muted-foreground text-center text-sm">No participants yet</p>
                  )}
                </div>
              </ScrollArea>
            )}
          </div>

          <div className="bg-muted/50 text-muted-foreground rounded-md px-3 py-2 text-xs">
            <p className="mb-1 font-medium">Permission levels:</p>
            <ul className="space-y-0.5">
              <li>
                <strong>Reader:</strong> Can view the use case fact sheet
              </li>
              <li>
                <strong>Editor:</strong> Can view and edit use case content
              </li>
              <li>
                <strong>Owner:</strong> Full control including managing access
              </li>
            </ul>
          </div>
        </div>

        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
